import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { blogService } from '../../../services/blogService';
import Analytics from './Analytics';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BlogManagement = () => {
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    imageUrl: '',
    categoryId: '',
    isPublished: false,
    publishedAt: ''
  });
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#3B82F6' });
  const contentRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsData, categoriesData] = await Promise.all([
        blogService?.getAllPosts(),
        blogService?.getAllCategories()
      ]);
      setPosts(postsData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      imageUrl: '',
      categoryId: '',
      isPublished: false,
      publishedAt: ''
    });
    setImageFile(null);
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post?.title,
      slug: post?.slug,
      content: post?.content,
      imageUrl: post?.imageUrl || '',
      categoryId: post?.categoryId || '',
      isPublished: post?.isPublished,
      publishedAt: post?.publishedAt ? new Date(post?.publishedAt)?.toISOString()?.slice(0, 16) : ''
    });
    setImageFile(null);
    setImagePreview(post?.imageUrl || '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu yazıyı silmek istediğinizden emin misiniz?')) return;

    try {
      await blogService?.deletePost(id);
      setPosts(posts?.filter(p => p?.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert(err?.message || 'Silme işlemi başarısız');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) return;

    try {
      await blogService?.deleteCategory(id);
      setCategories(categories?.filter(c => c?.id !== id));
    } catch (err) {
      console.error('Delete category failed:', err);
      alert(err?.message || 'Kategori silme işlemi başarısız');
    }
  };

  const handleToggleStatus = async (post) => {
    try {
      const updatedPost = await blogService?.updatePost(post?.id, {
        ...post,
        isPublished: !post?.isPublished,
        publishedAt: !post?.isPublished && !post?.publishedAt ? new Date()?.toISOString() : post?.publishedAt
      });
      setPosts(posts?.map(p => p?.id === updatedPost?.id ? updatedPost : p));
    } catch (err) {
      console.error('Toggle status failed:', err);
      alert(err?.message || 'Durum değiştirme işlemi başarısız');
    }
  };

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('image/')) {
      alert('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    if (file?.size > 10 * 1024 * 1024) {
      alert('Resim boyutu 10MB\'dan küçük olmalıdır');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleTitleChange = (e) => {
    const title = e?.target?.value;
    setFormData({
      ...formData,
      title,
      slug: blogService?.generateSlug(title)
    });
  };

  const insertFormatting = (before, after = '') => {
    const textarea = contentRef?.current;
    if (!textarea) return;

    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selectedText = formData?.content?.substring(start, end);
    const newText = formData?.content?.substring(0, start) + before + selectedText + after + formData?.content?.substring(end);
    
    setFormData({ ...formData, content: newText });
    
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + before?.length, end + before?.length);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    try {
      setUploading(true);

      let imageUrl = formData?.imageUrl;

      if (imageFile) {
        imageUrl = await blogService?.uploadCoverImage(imageFile);
      }

      const postData = {
        ...formData,
        imageUrl
      };

      let savedPost;
      if (editingPost) {
        savedPost = await blogService?.updatePost(editingPost?.id, postData);
        setPosts(posts?.map(p => p?.id === savedPost?.id ? savedPost : p));
      } else {
        savedPost = await blogService?.createPost(postData);
        setPosts([savedPost, ...posts]);
      }

      setShowModal(false);
      setImageFile(null);
      setImagePreview('');
    } catch (err) {
      console.error('Save failed:', err);
      setError(err?.message || 'Kaydetme işlemi başarısız');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory?.name?.trim()) {
      alert('Kategori adı gereklidir');
      return;
    }

    try {
      const created = await blogService?.createCategory(newCategory);
      setCategories([...categories, created]);
      setNewCategory({ name: '', description: '', color: '#3B82F6' });
      setShowCategoryModal(false);
    } catch (err) {
      console.error('Create category failed:', err);
      alert(err?.message || 'Kategori oluşturulamadı');
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'code-block',
    'list',
    'bullet',
    'link',
    'image'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Blog Yönetimi</h2>
          <p className="text-gray-600 mt-1">Blog yazılarını oluşturun ve yönetin</p>
        </div>
        {activeTab === 'posts' && (
          <Button onClick={handleCreate}>
            <Icon name="Plus" className="w-5 h-5 mr-2" />
            Yeni Yazı
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('posts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'posts' ?'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Yazılar
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'categories' ?'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Kategoriler
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'analytics' ?'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            İstatistikler
          </button>
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'posts' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Başlık
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    Henüz blog yazısı yok. İlk yazıyı oluşturun!
                  </td>
                </tr>
              ) : (
                posts?.map((post) => (
                  <tr key={post?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {post?.imageUrl && (
                          <img
                            src={post?.imageUrl}
                            alt={post?.title}
                            className="w-12 h-12 rounded object-cover mr-3"
                          />
                        )}
                        <div className="text-sm font-medium text-gray-900">
                          {post?.title}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {post?.category && (
                        <span
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                          style={{ backgroundColor: `${post?.category?.color}20`, color: post?.category?.color }}
                        >
                          {post?.category?.name}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(post)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer transition-all hover:opacity-80 ${
                          post?.isPublished
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' :'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                        title="Durumu değiştirmek için tıklayın"
                      >
                        {post?.isPublished ? 'Yayında' : 'Taslak'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {post?.publishedAt 
                        ? new Date(post.publishedAt)?.toLocaleDateString('tr-TR')
                        : new Date(post.createdAt)?.toLocaleDateString('tr-TR')
                      }
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(post)}
                        className="text-primary hover:text-primary-dark mr-4"
                      >
                        <Icon name="Edit" className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(post?.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Icon name="Trash2" className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'analytics' && <Analytics />}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowCategoryModal(true)}>
              <Icon name="Plus" className="w-5 h-5 mr-2" />
              Yeni Kategori
            </Button>
          </div>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori Adı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Renk
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oluşturulma Tarihi
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories?.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                      Henüz kategori yok. İlk kategoriyi oluşturun!
                    </td>
                  </tr>
                ) : (
                  categories?.map((category) => (
                    <tr key={category?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span
                            className="inline-flex px-3 py-1 text-sm font-semibold rounded-full"
                            style={{ backgroundColor: `${category?.color}20`, color: category?.color }}
                          >
                            {category?.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-6 h-6 rounded border border-gray-300"
                            style={{ backgroundColor: category?.color }}
                          ></div>
                          <span className="text-sm text-gray-600 font-mono">{category?.color}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {category?.createdAt ? new Date(category?.createdAt)?.toLocaleDateString('tr-TR') : '-'}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <button
                          onClick={() => handleDeleteCategory(category?.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Kategoriyi Sil"
                        >
                          <Icon name="Trash2" className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-primary to-primary-dark text-white p-6 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">
                    {editingPost ? 'Yazıyı Düzenle' : 'Yeni Blog Yazısı Oluştur'}
                  </h3>
                  <p className="text-primary-100 text-sm mt-1">
                    {editingPost ? 'Mevcut yazınızı güncelleyin' : 'Profesyonel bir blog yazısı oluşturun'}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <label className="flex items-center text-sm font-semibold text-gray-700">
                  <Icon name="FileText" className="w-4 h-4 mr-2 text-primary" />
                  Yazı Başlığı
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData?.title}
                  onChange={handleTitleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all text-gray-900 placeholder-gray-400"
                  placeholder="Örn: Dağ Bisikletinde Güvenlik İpuçları"
                />
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <label className="flex items-center text-sm font-semibold text-gray-700">
                  <Icon name="Link" className="w-4 h-4 mr-2 text-primary" />
                  URL Adresi (Slug)
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData?.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e?.target?.value })}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-gray-50 text-gray-900 font-mono text-sm"
                  placeholder="dag-bisikletinde-guvenlik-ipuclari"
                />
              </div>

              {/* Category Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="flex items-center text-sm font-semibold text-gray-700">
                    <Icon name="Tag" className="w-4 h-4 mr-2 text-primary" />
                    Kategori
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCategoryModal(true)}
                    className="text-xs text-primary hover:text-primary-dark font-medium flex items-center"
                  >
                    <Icon name="Plus" className="w-3 h-3 mr-1" />
                    Yeni Kategori
                  </button>
                </div>
                <select
                  value={formData?.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e?.target?.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all text-gray-900"
                >
                  <option value="">Kategori Seçin (Opsiyonel)</option>
                  {categories?.map(cat => (
                    <option key={cat?.id} value={cat?.id}>
                      {cat?.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Publication Date */}
              <div className="space-y-2">
                <label className="flex items-center text-sm font-semibold text-gray-700">
                  <Icon name="Calendar" className="w-4 h-4 mr-2 text-primary" />
                  Yayınlanma Tarihi
                </label>
                <input
                  type="datetime-local"
                  value={formData?.publishedAt}
                  onChange={(e) => setFormData({ ...formData, publishedAt: e?.target?.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all text-gray-900"
                  placeholder="Yayınlanma tarihini seçin"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Boş bırakılırsa, yazı yayınlandığında otomatik olarak şu anki tarih kullanılır
                </p>
              </div>

              {/* Cover Image */}
              <div className="space-y-2">
                <label className="flex items-center text-sm font-semibold text-gray-700">
                  <Icon name="Image" className="w-4 h-4 mr-2 text-primary" />
                  Kapak Görseli
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="md:col-span-2 cursor-pointer group">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-primary hover:bg-primary-50 transition-all">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-primary-200 transition-colors">
                          <Icon name="Upload" className="w-6 h-6 text-primary" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 mb-1">
                          {imageFile ? imageFile?.name : 'Kapak Görseli Yükle'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Sürükle-bırak veya tıklayarak seç
                        </span>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  {imagePreview && (
                    <div className="mt-3 relative">
                      <img
                        src={imagePreview}
                        alt="Kapak görseli önizlemesi"
                        className="w-full h-full object-cover rounded-lg border-2 border-gray-200"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Rich Text Editor */}
              <div className="space-y-2">
                <label className="flex items-center text-sm font-semibold text-gray-700">
                  <Icon name="FileEdit" className="w-4 h-4 mr-2 text-primary" />
                  Yazı İçeriği
                  <span className="text-red-500 ml-1">*</span>
                </label>
                
                {/* Formatting Toolbar */}
                <div className="bg-gray-50 border-2 border-gray-200 rounded-t-lg p-2 flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => insertFormatting('# ', '')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm font-semibold"
                    title="Başlık 1"
                  >
                    H1
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('## ', '')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm font-semibold"
                    title="Başlık 2"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('### ', '')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm font-semibold"
                    title="Başlık 3"
                  >
                    H3
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button
                    type="button"
                    onClick={() => insertFormatting('**', '**')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 font-bold text-sm"
                    title="Kalın"
                  >
                    <Icon name="Bold" className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('*', '*')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 italic text-sm"
                    title="İtalik"
                  >
                    <Icon name="Italic" className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('~~', '~~')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
                    title="Üstü Çizili"
                  >
                    <Icon name="Strikethrough" className="w-4 h-4" />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button
                    type="button"
                    onClick={() => insertFormatting('- ', '')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
                    title="Liste"
                  >
                    <Icon name="List" className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('1. ', '')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
                    title="Numaralı Liste"
                  >
                    <Icon name="ListOrdered" className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('> ', '')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
                    title="Alıntı"
                  >
                    <Icon name="Quote" className="w-4 h-4" />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button
                    type="button"
                    onClick={() => insertFormatting('[Link Metni](', 'https://ornek.com)')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm"
                    title="Link Ekle"
                  >
                    <Icon name="Link" className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('`', '`')}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm font-mono"
                    title="Kod"
                  >
                    <Icon name="Code" className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="relative">
                  <ReactQuill
                    theme="snow"
                    value={formData?.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white rounded-lg"
                    style={{ height: '400px', marginBottom: '50px' }}
                  />
                  <div className="absolute bottom-3 right-3 bg-white px-2 py-1 rounded border border-gray-200 text-xs text-gray-500">
                    {formData?.content?.length || 0} karakter
                  </div>
                </div>
              </div>

              {/* Published Status */}
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="isPublished"
                    checked={formData?.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e?.target?.checked })}
                    className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary mt-0.5"
                  />
                  <div className="ml-3">
                    <label htmlFor="isPublished" className="text-sm font-semibold text-gray-900 cursor-pointer flex items-center">
                      <Icon name="Eye" className="w-4 h-4 mr-1 text-primary" />
                      Yazıyı Hemen Yayınla
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      İşaretli değilse yazı taslak olarak kaydedilir
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                  <Icon name="AlertCircle" className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-6 border-t-2 border-gray-100">
                <p className="text-xs text-gray-500 flex items-center">
                  <Icon name="Lock" className="w-3 h-3 mr-1" />
                  Tüm veriler güvenli şekilde kaydedilir
                </p>
                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    disabled={uploading}
                    className="px-6"
                  >
                    <Icon name="X" className="w-4 h-4 mr-2" />
                    İptal
                  </Button>
                  <Button type="submit" disabled={uploading} className="px-6">
                    {uploading ? (
                      <>
                        <Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Icon name="Check" className="w-4 h-4 mr-2" />
                        {editingPost ? 'Değişiklikleri Kaydet' : 'Yazıyı Oluştur'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Creation Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Yeni Kategori Oluştur</h3>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <Icon name="X" className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Kategori Adı *
                </label>
                <input
                  type="text"
                  value={newCategory?.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e?.target?.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Örn: Dağ Bisikleti"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={newCategory?.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e?.target?.value })}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Kategori açıklaması (opsiyonel)"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Renk
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={newCategory?.color}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e?.target?.value })}
                    className="w-12 h-10 border-2 border-gray-200 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={newCategory?.color}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e?.target?.value })}
                    className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono"
                    placeholder="#3B82F6"
                  />
                </div>
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCategoryModal(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
                <Button
                  type="button"
                  onClick={handleCreateCategory}
                  className="flex-1"
                >
                  <Icon name="Plus" className="w-4 h-4 mr-2" />
                  Oluştur
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;